import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/content";

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[active];
  const typeLabel = current.type === "movie" ? "电影" : "电视剧";

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - ${item.genres.join(" ")}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl animate-fade-up" key={current.id}>
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-primary-950 text-xs font-bold">
              {typeLabel}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm">
              {current.region} · {current.year}
            </span>
            <span className="flex items-center gap-1 text-accent-400 text-sm font-semibold">
              <i className="ri-star-fill text-xs"></i>
              {current.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-background-50 font-heading leading-tight">
            {current.title}
          </h1>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3 md:line-clamp-2">
            {current.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="px-5 md:px-6 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-play-circle-line text-base"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="px-5 md:px-6 py-2.5 rounded-md bg-background-50/10 text-background-50 border border-background-50/30 text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{typeLabel}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-10 flex items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}